import React, {Suspense, lazy} from 'react';

import InflowSpinner from 'App/components/spinner/inflowSpinner';
import {connectToFeatureFlags} from 'App/features/featureFlags/featureFlagsSlice';
import {connectToUser} from 'App/features/user/userSlice';
import ResponsiveContentView from 'App/layout/components/responsiveContentView';
import {connectToLayout} from 'App/layout/layoutSlice';

import {LAYOUT_TYPES} from '../../common/pages_constants';

const RichMapContainer = lazy(
  () => import('../../common/features/rich_map/rich_map_container'),
);

class ContentView extends React.Component {
  mapReference = React.createRef();

  constructor(props) {
    super(props);
  }

  renderContent = content => {
    const {
      onMapConfigurationChange,
      onRequestingStatusChange,
      onItemsChanged,
      onFocusItemsChanged,
      focusedProject,
      focusedParcel,
      focusedLead,
      focusedLandUnit,
      inseeCodeFilter,
      onAutoBuildrClick,
      onShadowProjectPublished,
      isFullscreen,
    } = this.props;

    return React.Children.map(content, (child, index) => {
      return React.cloneElement(child, {
        index,
        onMapConfigurationChange,
        onRequestingStatusChange,
        onItemsChanged,
        onFocusItemsChanged,
        focusedProject,
        focusedParcel,
        focusedLead,
        focusedLandUnit,
        getXLSXForIcConstruction: this.getXLSXForIcConstruction,
        getMapScreenshot2D: this.getMapScreenshot2D,
        hasCustomBuildings3D: this.hasCustomBuildings3D,
        getMapScreenshot3D: this.getMapScreenshot3D,
        getAllResults: this.getAllResults,
        getMapOrthoScreenshot: this.getMapOrthoScreenshot,
        getParkingVisual: this.getParkingVisual,
        getInteriorDesignScreenshot: this.getInteriorDesignScreenshot,
        getStreetsScreenshot: this.getStreetsScreenshot,
        getSceneInput: this.getSceneInput,
        saveSelectedPLUResult: this.saveSelectedPLUResult,
        setGeoLimitZone: this.setGeoLimitZone,
        onToggleRuleVisibility: this.handleToggleRuleVisibility,
        inseeCodeFilter,
        getMapGroundPlanScreenshot: this.getMapGroundPlanScreenshot,
        getMapClippingFrontFacingScreenshot:
          this.getMapClippingFrontFacingScreenshot,
        onAutoBuildrClick,
        onShadowProjectPublished,
        onAddressSelect: this.onAddressSelect,
        generateInteriorDesigns: this.generateInteriorDesigns,
        isFullscreen,
        computeDivision: this.computeDivision,
        displayDivisionOption: this.displayDivisionOption,
        resetDivision: this.resetDivision,
        selectTenementByID: this.selectTenementByID,
      });
    })[0];
  };

  renderMap = () => {
    const {
      map2DConfiguration,
      isRequesting,
      focusedParcel,
      focusedProject,
      inseeCodeFilter,
      focusedLandUnit,
      focusedCity,
      focusedAddress,
      focusedLead,
      layoutType,
      isExperimentalFeaturesViewable,
      credentials,
      onFullScreenChange,
      map3DConfiguration,
      focusedTransaction,
      parcelInfoBox,
    } = this.props;

    const experimentalFeatures = credentials?.user?.experimental_features;

    if (!map2DConfiguration && !map3DConfiguration) {
      return null;
    }

    const spinner = isRequesting ? <InflowSpinner /> : null;
    const isBottomStreetView = layoutType !== LAYOUT_TYPES.FULL_SCREEN;
    return (
      <>
        {spinner}
        <Suspense fallback={<InflowSpinner />}>
          <RichMapContainer
            ref={this.mapReference}
            map2DConfiguration={map2DConfiguration}
            map3DConfiguration={map3DConfiguration}
            focusedParcel={focusedParcel}
            focusedProject={focusedProject}
            focusedLandUnit={focusedLandUnit}
            focusedTransaction={focusedTransaction}
            focusedLead={focusedLead}
            credentials={credentials}
            focusedCity={focusedCity}
            focusedAddress={focusedAddress}
            requesting={isRequesting}
            isBottomStreetView={isBottomStreetView}
            inseeCodeFilter={inseeCodeFilter}
            experimentalFeatures={
              isExperimentalFeaturesViewable && experimentalFeatures
            }
            onFullscreenChange={onFullScreenChange}
            parcelInfoBox={parcelInfoBox}
          />
        </Suspense>
      </>
    );
  };

  getXLSXForIcConstruction = icConstruction => {
    return this.mapReference.current.getXLSXForIcConstruction(icConstruction);
  };

  getMapScreenshot2D = config => {
    return this.mapReference.current.getScreenshot2D(config);
  };

  computeDivision = settings => {
    return this.mapReference.current.computeDivision(settings);
  };
  resetDivision = registry => {
    return this.mapReference.current.resetDivision(registry);
  };

  displayDivisionOption = (idsToRemove, subparcelsToAdd, remainsToAdd) => {
    return this.mapReference.current.displayDivisionOption(
      idsToRemove,
      subparcelsToAdd,
      remainsToAdd,
    );
  };

  hasCustomBuildings3D = () => {
    return this.mapReference.current.hasCustomBuildings3D();
  };

  getMapScreenshot3D = () => {
    return this.mapReference.current.getScreenshot3D();
  };

  getMapGroundPlanScreenshot = overlay => {
    return this.mapReference.current.getGroundPlanScreenshot(overlay);
  };

  generateInteriorDesigns = buildingListInteriorParams => {
    return this.mapReference.current.generateInteriorDesigns(
      buildingListInteriorParams,
    );
  };

  getMapClippingFrontFacingScreenshot = (width, height) => {
    return this.mapReference.current.getClippingFrontFacingScreenshot(
      width,
      height,
    );
  };

  getAllResults = () => {
    return this.mapReference.current.getAllResults();
  };

  getMapOrthoScreenshot = () => {
    return this.mapReference.current.getOrthoScreenshot();
  };

  getParkingVisual = overlay => {
    return this.mapReference.current.getParkingVisual(overlay);
  };

  getInteriorDesignScreenshot = size => {
    return this.mapReference.current.getInteriorDesignScreenshot(size);
  };

  getStreetsScreenshot = () => {
    return this.mapReference.current.getStreetsScreenshot();
  };

  getSceneInput = () => {
    return this.mapReference.current.getSceneInput();
  };

  saveSelectedPLUResult = () => {
    this.mapReference.current.saveSelectedPLUResult();
  };

  setGeoLimitZone = geometry => {
    this.mapReference.current.setGeoLimitZone(geometry);
  };

  handleToggleRuleVisibility = (ruleNumber, isExpanded) => {
    this.mapReference.current.handleToggleRuleVisibility(
      ruleNumber,
      isExpanded,
    );
  };

  getPosition = () => {
    return this.mapReference.current.getPosition();
  };

  getRadius = () => {
    return this.mapReference.current.getRadius();
  };

  onAddressSelect = address => {
    return this.mapReference.current.onAddressSelect(address);
  };

  onCitySelect(address) {
    return this.mapReference.current.onCitySelect(address);
  }

  handleAutoBuildrClick = () => {
    this.mapReference.current.handleAutoBuildrClick();
  };

  handleShadowProjectPublished = callback => {
    this.mapReference.current.handleShadowProjectPublished(callback);
  };

  selectTenementByID = id => {
    return this.mapReference.current.selectTenementByID(id);
  };

  render() {
    const {children} = this.props;

    const mergedContent = this.renderContent(children);
    const map = this.renderMap();

    return <ResponsiveContentView content={mergedContent} map={map} />;
  }
}

export default connectToFeatureFlags(
  connectToLayout(connectToUser(ContentView)),
);
